let audioContext: AudioContext | null = null;
let isMute: boolean = false;

export function initAudioContext() {
  if (!audioContext) {
    audioContext = new AudioContext();
    audioContext.resume();
    const mp3Names = ["bg0", "bg04", "bg125", "bg34","bg7", "bg8", "bg9", "se5", "se6"];
    mp3Names.forEach((name) => {
      loadMP3(name);
    });
    const wavNames = ["se1", "se2","se3", "se4", "bg6"];
    wavNames.forEach((name) => {
      loadWAV(name);
    });
  }
}

const seBuffers: Record<string, AudioBuffer> = {};

interface PlayingBGM {
  source: AudioBufferSourceNode;
  gainNode: GainNode;
  name: string;
}

let currentBGM: PlayingBGM | null = null;

async function loadMP3(name: string) {
  const res = await fetch(`${process.env.PUBLIC_URL}/sound/${name}.mp3`);
  const arrayBuffer = await res.arrayBuffer();
  seBuffers[name] = await audioContext!.decodeAudioData(arrayBuffer);
}

async function loadWAV(name: string) {
  const res = await fetch(`${process.env.PUBLIC_URL}/sound/${name}.wav`);
  const arrayBuffer = await res.arrayBuffer();
  seBuffers[name] = await audioContext!.decodeAudioData(arrayBuffer);
}

export function playBGM(
  name: string,
  options?: {
    duration?: number;
    volume?: number;
    fadeInDuration?: number;
    fadeOutDuration?: number;
    loop?: boolean;
  }
) {
  const buffer = seBuffers[name];
  if (!buffer || !audioContext) return;

  // 既にBGMが再生中なら停止
  if (currentBGM) {
    stopBGM();
  }

  const source = audioContext.createBufferSource();
  source.buffer = buffer;

  const gainNode = audioContext.createGain();

  const now = audioContext.currentTime;
  const baseVolume = isMute ? 0 : (options?.volume ?? 0.05);
  source.loop = options?.loop ?? false;

  gainNode.gain.setValueAtTime(0, now);

  if (options?.fadeInDuration) {
    gainNode.gain.linearRampToValueAtTime(baseVolume, now + options.fadeInDuration);
  } else {
    gainNode.gain.setValueAtTime(baseVolume, now);
  }

  if (options?.fadeOutDuration && options.duration) {
    const fadeOutStart = now + options.duration - options.fadeOutDuration;
    if (fadeOutStart > now) {
      gainNode.gain.setValueAtTime(baseVolume, fadeOutStart);
      gainNode.gain.linearRampToValueAtTime(0.0, fadeOutStart + options.fadeOutDuration);
    }
  }

  source.connect(gainNode);
  gainNode.connect(audioContext.destination);

  // 現在のBGMとして登録
  currentBGM = { source, gainNode, name };

  source.onended = () => {
    source.disconnect();
    gainNode.disconnect();
    if (currentBGM?.source === source) {
      currentBGM = null;
    }
  };

  if (options?.duration) {
    source.start(now, 0, options.duration);
  } else {
    source.start(now);
  }
}

/**
 * 再生中のBGMを即座に停止
 */
export function stopBGM() {
  if (!audioContext || !currentBGM) return;

  try {
    currentBGM.source.stop();
  } catch (e) {
    // 既に停止している場合のエラーを無視
  }
  currentBGM.source.disconnect();
  currentBGM.gainNode.disconnect();
  currentBGM = null;
}

/**
 * 再生中のBGMをフェードアウトして停止
 * @param duration フェードアウト時間（秒）デフォルト: 1.0秒
 */
export function fadeOutBGM(duration: number = 1.0) {
  if (!audioContext || !currentBGM) return;

  const now = audioContext.currentTime;
  const bgm = currentBGM;

  const currentVolume = bgm.gainNode.gain.value;
  bgm.gainNode.gain.cancelScheduledValues(now);
  bgm.gainNode.gain.setValueAtTime(currentVolume, now);
  bgm.gainNode.gain.linearRampToValueAtTime(0.0, now + duration);

  // フェードアウト後に停止
  setTimeout(() => {
    try {
      bgm.source.stop();
    } catch (e) {
      // 既に停止している場合のエラーを無視
    }
    bgm.source.disconnect();
    bgm.gainNode.disconnect();
    if (currentBGM === bgm) {
      currentBGM = null;
    }
  }, duration * 1000);
}

/**
 * 現在再生中のBGM名を取得
 * @returns 再生中のBGM名、再生中でなければnull
 */
export function getCurrentBGM(): string | null {
  return currentBGM?.name ?? null;
}

export function playSE(name: string) {
  const buffer = seBuffers[name];
  if (!buffer || !audioContext) return;

  const source = audioContext.createBufferSource();
  source.buffer = buffer;

  const gainNode = audioContext.createGain();

  const now = audioContext.currentTime;
  const baseVolume = isMute ? 0 : 0.1;
  gainNode.gain.setValueAtTime(baseVolume, now);

  source.connect(gainNode);
  gainNode.connect(audioContext.destination);

  source.onended = () => {
    source.disconnect();
    gainNode.disconnect();
  };
  source.start(now);
}

/**
 * 音量変更
 */
export function toggleMute() {
  isMute = !isMute;

  if (!audioContext) return isMute;

  // 現在BGMがある場合のみ音量を変更
  if (currentBGM) {
    const now = audioContext.currentTime;
    const gain = currentBGM.gainNode.gain;
    gain.cancelScheduledValues(now);

    if (isMute) {
      gain.linearRampToValueAtTime(0, now + 0.05);
    } else {
      gain.linearRampToValueAtTime(0.1, now + 0.2);
    }
  }

  return isMute;
}
